import { useGetSuperAdminProfitability } from '@agos/api-client/platform'
import {
  PROFITABILITY_PRESETS,
  T,
  WHOLE_DEFAULT_WINDOW,
  formatCents,
  formatCount,
  reportWindowParams,
  tt,
  type ReportWindow,
} from '@agos/shared'
import { useMemo, useState } from 'react'
import { PageHeader, ReadFailed, ReportWindowBar, Spinner, StatCard } from '@agos/ui'
import { Building2, PiggyBank, Receipt, TrendingUp } from 'lucide-react'

import {
  PROFIT_SORTS,
  chargeNote,
  houseShareNote,
  isCharge,
  isLoss,
  isOverAllocatedProfit,
  marginNote,
  costBreakdownOf,
  dailyCostHeight,
  dailyCostTotals,
  profitabilityOf,
  sortedFranchises,
  type ProfitSortKey,
} from './profitability'

/**
 * What each franchise earns the platform, and what each partner's share comes to.
 *
 * Two things legacy gets wrong and this does not — a margin is shown only where there is revenue to
 * compute one from (**FB-S01**), and a negative payout is drawn and described as a share of a loss
 * rather than in the colour that means *paid to you* (**FB-S02**).
 */
export function ProfitabilityPage() {
  /**
   * §3.84 - the period this report is about.
   *
   * **The endpoint has taken `from` and `to` since it was written** and this screen passed neither,
   * so it answered one question - the server's default, the month to date - and the operator had no
   * way to ask another. Legacy's page has a date range and five presets. Starting at
   * `WHOLE_DEFAULT_WINDOW` sends nothing, which is what keeps the figures on first load identical to
   * what they were before this entry.
   */
  const [window, setWindow] = useState<ReportWindow>(WHOLE_DEFAULT_WINDOW)
  const { data, isLoading, isError } = useGetSuperAdminProfitability(reportWindowParams(window))
  const report = useMemo(() => profitabilityOf(data), [data])
  const series = useMemo(() => dailyCostTotals(report.dailyCost), [report.dailyCost])
  /* §3.112 - which franchise is losing us money. Legacy's table sorts on five columns and this screen
   * listed them in whatever order the server sent, which on three franchises is tolerable and on
   * thirty is not. Biggest profit first, because that is the order somebody scanning a list of
   * franchises expects. */
  const [sortKey, setSortKey] = useState<ProfitSortKey>('profit')
  const [descending, setDescending] = useState(true)
  const franchises = useMemo(
    () => sortedFranchises(report.perFranchise, sortKey, descending),
    [report.perFranchise, sortKey, descending],
  )

  return (
    <main className="space-y-3" data-profitability>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader
        title={tt('Ziskovosť')}
        description={tt('Čo každá franšíza zarobila a čo stála.')}
      />

      {/* §3.84 - the period, above the figures it governs rather than beside them: every number on
        * this screen is *for* this window, and a control tucked into a corner reads as a filter over
        * a fixed report. The window the server chose is named when nothing is selected, so no figure
        * on the page is ever unlabelled. */}
      <ReportWindowBar
        window={window}
        onChange={setWindow}
        presets={PROFITABILITY_PRESETS}
        defaultNote={
          report.window.from === ''
            ? tt('Predvolené obdobie serveru')
            : `${report.window.from} - ${report.window.to}`
        }
      />
      {/* §3.82 - a screen that cannot load its data must not render its empty state.
        * Discarding `isLoading` and `isError` meant *"Za toto obdobie nie sú žiadne dáta"* appeared while
        * the request was in flight and again when it failed - so a broken load read as a quiet
        * period, which is §3.52's argument applied to the six screens it did not reach. */}
      {isError && (
        <ReadFailed marker="profitability-error"><T>Ziskovosť sa nepodarilo načítať.</T></ReadFailed>
      )}
      {isLoading && (
        <div className="py-10 flex justify-center" data-profitability-loading>
          <Spinner />
        </div>
      )}


      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-profitability-totals>
        {/* §3.81 - the shared card. Profit takes its colour from its sign, which is the one thing
          * a reader of this screen looks for first. */}
        <StatCard
          testId="revenue"
          label={tt('Tržby')}
          value={formatCents(report.totals.revenueCents)}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          testId="cost"
          label={tt('Náklady')}
          value={formatCents(report.totals.totalCostCents)}
          icon={Receipt}
          color="orange"
        />
        <StatCard
          testId="profit"
          label={tt('Zisk')}
          value={formatCents(report.totals.profitCents)}
          icon={PiggyBank}
          color={report.totals.profitCents < 0 ? 'red' : 'emerald'}
        />
        <StatCard
          testId="house"
          label={tt('Podiel platformy')}
          value={formatCents(report.totals.housePayoutCents)}
          icon={Building2}
          color="violet"
        />
      </div>

      {/* §3.52 - an explicit nothing. The totals above are all noughts when there is no data, which
        * is indistinguishable from a platform that earned nothing this period, and the operator has
        * no other way to tell a quiet month from a failed load. */}
      {!isLoading && !isError && report.perFranchise.length === 0 && (
        <p
          className="rounded-2xl border border-dashed border-border p-4 text-xs text-muted-foreground"
          data-profitability-empty
        >
          <T>Za toto obdobie nie sú žiadne údaje o ziskovosti.</T>
        </p>
      )}

      {/* §3.112 - the order control. A row of keys rather than a table header, because these rows are
        * cards: each carries a cost breakdown and a line per partner, and flattening that into a table
        * to gain a sortable header would lose the part of the screen that answers *why*. */}
      {!isLoading && !isError && franchises.length > 1 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px]" data-profit-sort={sortKey}>
          <span className="text-muted-foreground">
            <T>Zoradiť</T>
          </span>
          {PROFIT_SORTS.map((sort) => (
            <button
              key={sort.key}
              type="button"
              className={`rounded-lg border px-2 py-1 font-semibold ${
                sortKey === sort.key ? 'border-primary bg-primary/10 text-primary' : 'border-border'
              }`}
              data-profit-sort-key={sort.key}
              onClick={() => setSortKey(sort.key)}
            >
              {sort.label}
            </button>
          ))}
          <button
            type="button"
            className="rounded-lg border border-border px-2 py-1 font-semibold"
            data-profit-sort-dir={descending ? 'desc' : 'asc'}
            onClick={() => setDescending((current) => !current)}
          >
            {descending ? tt('Najviac najprv') : tt('Najmenej najprv')}
          </button>
        </div>
      )}

      {franchises.map((franchise) => (
        <section
          key={franchise.franchiseSlug}
          className="rounded-2xl border border-border p-3 space-y-1"
          data-profit-franchise={franchise.franchiseSlug}
        >
          <p className="text-sm font-semibold">{franchise.franchiseName}</p>
          <p className="text-xs">
            {formatCents(franchise.revenueCents)} - {formatCents(franchise.totalCostCents)} ={' '}
            <span
              className={isLoss(franchise) ? 'font-bold text-red-500' : 'font-bold'}
              data-profit={isLoss(franchise) ? 'loss' : 'gain'}
            >
              {formatCents(franchise.profitCents)}
            </span>{' '}
            &middot; <span data-margin>{marginNote(franchise)}</span>
          </p>
          {/* §3.41 - the three components of that cost, which the endpoint has always answered and
            * this screen showed only the total of. Text rather than legacy's `title` tooltip: a
            * tooltip is invisible on a touch screen and to a screen reader, and it is where legacy
            * lost `costModules` - it names API and hardware and stops. */}
          <p className="text-[11px] text-muted-foreground" data-cost-breakdown>
            {costBreakdownOf(franchise).map((part, index) => (
              <span key={part.label} data-cost-part={part.label}>
                {index > 0 ? ' · ' : ''}
                {part.label} {formatCents(part.cents)}
              </span>
            ))}
          </p>
          {/* §3.10 - said whenever there are partners at all, rather than only when the house keeps
            * something. `houseSharePct` clamped at nought and this line was drawn only for `> 0`, so
            * an over-allocation and a correct 100 % both rendered **nothing** and were
            * indistinguishable. The attribute carries which, because that is the distinction. */}
          {houseShareNote(franchise) !== '' && (
            <p
              className={`text-[11px] ${isOverAllocatedProfit(franchise) ? 'font-semibold text-red-500' : 'text-muted-foreground'}`}
              data-house-share
              data-house-over={isOverAllocatedProfit(franchise) ? 'yes' : 'no'}
            >
              {houseShareNote(franchise)}
            </p>
          )}
          {franchise.partnerSplit.map((split) => (
            <p
              key={split.partnerId}
              className={`text-[11px] ${isCharge(split) ? 'text-red-500' : 'text-emerald-500'}`}
              data-partner-split={String(split.partnerId)}
              data-partner-charge={isCharge(split) ? 'yes' : 'no'}
            >
              {split.partnerName} &middot; {formatCount(split.sharePct)} % &middot; {formatCents(split.payoutCents)}
              {chargeNote(split) !== '' && <span data-charge-note> - {chargeNote(split)}</span>}
            </p>
          ))}
        </section>
      ))}

      {/* §3.41 - the server's own per-partner aggregate. The cards above render a split per
        * franchise, so a partner holding shares in three of them appears three times and *what do we
        * owe them* was a sum done by eye; both of the owner's partners hold shares in all three. */}
      {report.perPartner.length > 0 && (
        <section className="rounded-2xl border border-border p-3 space-y-1" data-partner-totals>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Na partnera, spolu</T>
          </p>
          {report.perPartner.map((partner) => (
            <p
              key={partner.partnerId}
              className="text-xs"
              data-partner-total={String(partner.partnerId)}
            >
              {partner.partnerName} &middot;{' '}
              <span className="font-bold" data-partner-total-payout>
                {formatCents(partner.payoutCents)}
              </span>
              {/* Said because one row summing three shares would otherwise read as one share. */}
              {partner.franchises > 1 && (
                <span className="text-muted-foreground" data-partner-total-scope>
                  {' '}
                  - {formatCount(partner.franchises)} <T>franšíz</T>
                </span>
              )}
            </p>
          ))}
        </section>
      )}

      {/* §3.41 - the per-day cost series. Divs and inline styles, the same way `StatisticsPage`
        * draws its heat grid: there is no charting dependency in any `package.json` and that is
        * deliberate. The summary line is legacy's, and carries the finding inside the series - how
        * much of the cost is metered usage rather than a standing charge. */}
      {report.dailyCost.length > 0 && (
        <section className="rounded-2xl border border-border p-3 space-y-2" data-daily-cost>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <T>Náklady po dňoch</T>
            </p>
            <p className="text-[11px] text-muted-foreground" data-daily-cost-summary>
              {formatCents(series.totalCents)}
              {' · '}
              <T>variabilné</T> {formatCents(series.variableCents)}
              {' · '}
              <T>paušál</T> {formatCents(series.recurringCents)}
            </p>
          </div>
          <div className="flex items-end gap-px h-16" data-daily-cost-bars>
            {report.dailyCost.map((day) => (
              <div
                key={day.day}
                title={`${day.day} - ${formatCents(day.totalCents)}`}
                data-daily-cost-bar={day.day}
                className="flex-1 min-w-[2px] rounded-t bg-primary/60"
                style={{ height: `${dailyCostHeight(day, series.peakCents)}%` }}
              />
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground" data-daily-cost-window>
            {report.window.from === '' ? '' : `${report.window.from} - ${report.window.to}`}
          </p>
        </section>
      )}
    </main>
  )
}

